import { Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { WeekSubmitPartition } from './timesheet-week';

interface Props {
  /** What a Submit click will actually flip, skip, and leave alone — produced
   *  by `partitionWeekForSubmit` from the SAME lock predicate the grid paints
   *  cells with, so this button can never offer (or refuse) work the server
   *  would treat differently. */
  partition: WeekSubmitPartition;
  /** False while the target user is still resolving (delegation fetch in
   *  flight) — there is nobody to submit for yet. */
  hasTargetUser: boolean;
  isPending: boolean;
  isDelegating: boolean;
  /** First name of the user being submitted for, when delegating. */
  displayFirstName?: string;
  onSubmit: () => void;
}

/**
 * The week-view Submit button, with the tooltip that discloses what the click
 * will really do.
 *
 * Extracted from the timesheets route so the gating below is reachable from a
 * component test. That gating is where half of the 2026-09-07 production
 * incident lived: the button used to be disabled whenever ANY editable row in
 * the week sat in a closed period, so a week straddling a closed August and an
 * open September (31 Aug 2026 was a Monday) could not be submitted at all —
 * the request never left the browser, and the server-side fix that filters
 * closed rows was invisible to users.
 *
 * The rule the tests pin: closed rows are SKIPPED, never a veto. They are
 * excluded from `submittableCount` (so the label and the server agree on the
 * count), disclosed in the tooltip, and the button stays enabled as long as
 * anything at all remains submittable.
 */
export function TimesheetSubmitButton({
  partition,
  hasTargetUser,
  isPending,
  isDelegating,
  displayFirstName,
  onSubmit,
}: Props) {
  const {
    hasSubmittableInRange,
    submittableCount,
    emptyEditableCount,
    alreadySubmittedCount,
    periodClosedCount,
  } = partition;

  const prefix = isDelegating && displayFirstName ? `${displayFirstName}'s ` : '';

  // While the request is in flight, swap the label for an explicit progress
  // message so users get feedback during processing (the disabled state alone
  // reads as "stuck"). Otherwise the label reflects scope: "Submit N" when the
  // click will NOT take the whole week — rows already submitted, or rows the
  // server will skip because their period has closed (a straddling week) —
  // and "Submit week" when nothing is being left behind. The delegation
  // prefix is preserved across both shapes.
  const label = (() => {
    if (isPending) return 'Submitting…';
    if (hasSubmittableInRange && (alreadySubmittedCount > 0 || periodClosedCount > 0)) {
      return `Submit ${prefix}${submittableCount}`;
    }
    return `Submit ${prefix}week`;
  })();

  // Empty (blank-hours) copied rows and rows whose period has closed are both
  // SKIPPED by submit rather than blocking it, so both are disclosed here —
  // the count the button flips excludes them.
  const tooltip = (() => {
    const emptyNote =
      emptyEditableCount > 0
        ? ` ${emptyEditableCount} empty entr${emptyEditableCount === 1 ? 'y' : 'ies'} will be skipped.`
        : '';
    const closedNote =
      periodClosedCount > 0
        ? ` ${periodClosedCount} entr${periodClosedCount === 1 ? 'y is' : 'ies are'} in a closed period and will be skipped.`
        : '';
    if (!hasSubmittableInRange) {
      // A fully-closed week is the actionable half when a week is both closed
      // and empty — filling in hours would not make it submittable.
      if (periodClosedCount > 0) {
        return 'Nothing to submit — the editable entries this week are in a closed period.';
      }
      return emptyEditableCount > 0
        ? 'Nothing to submit — the editable entries this week have no hours yet.'
        : 'Nothing to submit — no editable entries this week.';
    }
    const base = `Submits ${submittableCount} entr${submittableCount === 1 ? 'y' : 'ies'}.`;
    const alreadyNote =
      alreadySubmittedCount > 0 ? ` ${alreadySubmittedCount} already submitted on this week.` : '';
    return `${base}${alreadyNote}${emptyNote}${closedNote}`;
  })();

  return (
    /* Tooltip discloses the actual scope of the click — with partial
      submission live, "Submit week" can mean "flip the 3 editable entries and
      leave the 4 already-submitted ones alone." Users should see what they're
      committing to before they click. The button is wrapped in a span trigger
      so the tooltip still surfaces when the button is disabled — disabled
      elements don't emit hover events, so a native `title` (or an asChild
      trigger on the button itself) goes silent exactly when the explanation
      matters most (e.g. "this period is locked"). */
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" data-testid="submit-week-tooltip-trigger">
            <Button
              size="sm"
              onClick={onSubmit}
              // NOT gated on "some row is locked": the server FILTERS closed
              // rows out of the submit rather than refusing it, so a
              // straddling week must stay submittable for the rows that will
              // really go. `hasSubmittableInRange` already excludes them.
              disabled={!hasTargetUser || isPending || !hasSubmittableInRange}
            >
              {isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
              {label}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
